import React, { useEffect, useState } from 'react'
import { Breadcrumb } from '../../../../components/ui/breadcrumb'
import toast, { Toaster } from 'react-hot-toast'
import { Label } from '../../../../components/ui/label'
import { Combobox } from '../../../../components/ui/combobox'
import { Route, Routes, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux'
import useAuth from '../../../../hooks/useAuth'
import { Button } from '../../../../components/ui/button'
import { Loader2 } from 'lucide-react'
import axios from '../../../../lib/axios'
import Detail from './kassa/Detail'
import { useTranslation } from 'react-i18next'
import useKassaSettingsStore from '../../../../zustand/useKassaSettingsStore'

const KassaSettings = () => {
  const navigate = useNavigate()
  const { auth } = useAuth()
  const { t } = useTranslation()
  const partners = useSelector((state: RootState) => state.partners)
  const {
    selected_partner, setselected_partner,
    selected_kassa, setselected_kassa,
    kassas_list, setkassas_list,
  } = useKassaSettingsStore()

  const [is_loading_kassas, setis_loading_kassas] = useState(false)

  useEffect(() => {
    if (selected_partner !== null) return
    const filtered = Array.isArray(partners) ? partners : []
    if (filtered.length === 1) {
      handlePickPartner(filtered[0].relnr.toString())
    }
  }, [partners])

  const handlePickPartner = async (relnr: string) => {
    try {
      if (selected_partner !== relnr) setselected_kassa(null)
      setselected_partner(relnr)
      setkassas_list(null)
      setis_loading_kassas(true)
      const { data } = await axios.get('kassa/get', {
        params: { token: auth.data?.token }
      })
      if (data.errorcode !== 0) { throw new Error(data.message) }
      const all: Kassa[] = Array.isArray(data.object) ? data.object : []
      const partnerRelnr = parseFloat(relnr)
      const list = all.filter((k) => k.relnr === partnerRelnr)
      setkassas_list(list)
      if (list.length === 1) {
        setselected_kassa(list[0].kassaid.toString())
      }
    } catch (error) {
      console.log(error)
      toast.error(t('settings.toast_error_p2'), { id: 'kassas' })
    } finally {
      setis_loading_kassas(false)
    }
  }

  const partnersArr = Array.isArray(partners)
    ? partners.map((item) => ({ value: item.relnr, label: `${item.name} - ${item.relnr}` }))
    : []

  const kassasArr = Array.isArray(kassas_list)
    ? kassas_list.map((item) => ({
        value: item.kassaid,
        label: `${item.omschrijving || item.cn} - ${item.kassaid}`
      }))
    : []

  return (
    <>
      <Routes>
        <Route path=":kassaid" element={<Detail />} />
        <Route path="" element={
          <>
            <Toaster />
            <div className='overflow-auto relative w-full p-4 md:p-8'>
              <Breadcrumb items={[
                { title: <p onClick={() => { navigate('/instellingen') }} className='cursor-pointer'>{t('settings.title')}</p> },
                { title: t('settings.kassa_settings_title') }
              ]} />
              <h1 className='text-2xl font-semibold'>{t('settings.kassa_settings_title')}</h1>

              <div className='flex flex-col gap-4 p-4 shadow-sm border rounded-sm mt-4 md:mt-8'>
                <div className='grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4'>
                  <div>
                    <Label htmlFor='partner'>{t('settings.partner')}</Label>
                    <Combobox
                      id='partner'
                      disabled={Array.isArray(partners) && partners.length === 1}
                      selectedValue={selected_partner ?? undefined}
                      onValueChange={(value) => { handlePickPartner(value.value.toString()) }}
                      options={partnersArr}
                      placeholder={t('settings.partner_placeholder')}
                      input_placeholder={t('settings.search_placeholder')}
                    />
                  </div>
                  <div>
                    <Label htmlFor='kassa'>{t('settings.kassa')}</Label>
                    <Combobox
                      id='kassa'
                      disabled={!Array.isArray(kassas_list) || is_loading_kassas}
                      selectedValue={selected_kassa ?? undefined}
                      onValueChange={(value) => { setselected_kassa(value.value.toString()) }}
                      options={kassasArr}
                      placeholder={is_loading_kassas ? t('settings.search_placeholder') : t('settings.kassa_settings_placeholder')}
                      input_placeholder={t('settings.search_placeholder')}
                    />
                  </div>
                </div>

                <Button
                  onClick={() => {
                    if (selected_kassa) {
                      const found = kassas_list?.find(k => k.kassaid.toString() === selected_kassa)
                      navigate(selected_kassa, { state: { kassa: found ?? null } })
                    }
                  }}
                  disabled={!selected_kassa || is_loading_kassas}
                  className='w-fit gap-2'
                >
                  {is_loading_kassas ? <Loader2 className='animate-spin' /> : t('settings.search')}
                </Button>
              </div>

              {Array.isArray(kassas_list) && kassas_list.length === 0 && !is_loading_kassas && selected_partner && (
                <p className='mt-8 text-neutral-500 text-sm'>{t('settings.kassa_settings_empty')}</p>
              )}
            </div>
          </>
        } />
      </Routes>
    </>
  )
}

export default KassaSettings
